import { FirebaseUser } from '../../firebase';
import { roleConfigAggregationRuleHeight } from '../components/tables/playerSimpleTable/RoleConfigTable';
import { InitialTeamDataPoint, TeamDataPoint } from '../components/teamView/TeamView';
import { trackEvent } from '../services/server/analytics/trackEvent';
import { positionToClosestPositions } from '../static/constants/positions';
import { FormationConfig } from '../types/formation';
import { PlatformPosition } from '../types/position';
import { ArchivedTeam, RoleConfigTeam, RoleConfigTeamPlayers, Team, TeamPlayers } from '../types/team';


interface TableNode extends TeamDataPoint {
  leftPx: number;
  topPx: number;
  originalLeftPx: number;
  originalTopPx: number;
  isCentered: boolean;
  movementResistance: {
    horizontal: number;
    vertical: number;
  };

  safeMarginLeft: number; // how much we can move left without colliding with other nodes or edges
  safeMarginRight: number; // how much we can move right without colliding with other nodes or edges
  safeMarginTop: number; // how much we can move up without colliding with other nodes or edges
  safeMarginBottom: number; // how much we can move down without colliding with other nodes or edges

  numberOfXRelaxations: number; // how many times we have relaxed in X direction in this iteration - we want to avoid loops of relaxation into collisions
  numberOfYRelaxations: number; // how many times we have relaxed in Y direction in this iteration - we want to avoid loops of relaxation into collisions
  numberOfHeightRelaxations: number; // how many times we have relaxed in height in this iteration - we want to avoid loops of relaxation into collisions

  // not used by simulation, but used in TeamView to determine if expansions should re-trigger the simulation
  safeMarginLeftToNodes: number; // how much we can move left without colliding with other nodes
  safeMarginRightToNodes: number; // how much we can move right without colliding with other nodes
}


interface NodeMovement {
  moveLeft: number;
  moveRight: number;
  moveUp: number;
  moveDown: number;
  heightDecrease: boolean;
}


export const resolveTableCollisions = (
  nodes: InitialTeamDataPoint[],
  tableWidth: number,
  tableRowHeight: number,
  containerWidth: number,
  containerHeight: number,
  isRoleConfigTable: boolean,

  MAX_ITERATIONS: number = 100,
  HEIGHT_CAPPING_STARTING_ITERATION: number = 5, // we start height capping after this many iterations (or after x stable iterations)
  MOMENTUM_FACTOR: number = 0.7, // will start at this, and then decrease to half of this value over time

  MAX_POSITION_DEVIATION_X: number = 10, // percentage
  MAX_POSITION_DEVIATION_Y: number = 10, // percentage

  SOFT_MARGIN_X: number = 2, // percentage
  SOFT_MARGIN_Y: number = 2, // percentage
  SOFT_MARGIN_EDGES_X: number = 4, // percentage
  SOFT_MARGIN_EDGES_Y: number = 3, // percentage

  COLLISION_RESOLUTION_PRECISION_X: number = 0.25, // percentage (must be less than SOFT_MARGIN_X and SOFT_MARGIN_EDGES_X)
  COLLISION_RESOLUTION_PRECISION_Y: number = 0.25, // percentage (must be less than SOFT_MARGIN_Y and SOFT_MARGIN_EDGES_Y)

  DIRECTION_RESOLUTION_X: number = 10, // if original positions differ more than this, we use the original positions to determine the direction of collisions
  DIRECTION_RESOLUTION_Y: number = 10, // if original positions differ more than this, we use the original positions to determine the direction of collisions

  SAFE_MARGIN: number = 1, // additional margin in pixels needed in order to relax nodes
  CLOSE_TO_RESOLVE_OVERLAP_THRESHOLD: number = 1, // if we are this close in pixels to resolving the overlap, we don't apply resistance
  MOVEMENT_RESOLUTION_PRECISION: number = 0.01, // we ignore movements smaller than this value in pixels
): TeamDataPoint[] => {

  const updatedNodes = [...nodes];

  let iterations = 0;
  let stableIterationCount = 0;
  let hasCollisions = true;
  let heightCappingHasStarted = false;

  let momentumFactorToUse = MOMENTUM_FACTOR;

  const MAX_POSITION_DEVIATION_X_PIXELS = (MAX_POSITION_DEVIATION_X / 100) * containerWidth;
  const MAX_POSITION_DEVIATION_Y_PIXELS = (MAX_POSITION_DEVIATION_Y / 100) * containerHeight;
  let maxPositionDeviationXPixels = MAX_POSITION_DEVIATION_X_PIXELS;
  let maxPositionDeviationYPixels = MAX_POSITION_DEVIATION_Y_PIXELS;

  const SOFT_MARGIN_X_PIXELS = (SOFT_MARGIN_X / 100) * containerWidth;
  const SOFT_MARGIN_Y_PIXELS = (SOFT_MARGIN_Y / 100) * containerHeight;
  const SOFT_MARGIN_EDGES_X_PIXELS = (SOFT_MARGIN_EDGES_X / 100) * containerWidth;
  const SOFT_MARGIN_EDGES_Y_PIXELS = (SOFT_MARGIN_EDGES_Y / 100) * containerHeight;
  const COLLISION_RESOLUTION_PRECISION_X_PIXELS = (COLLISION_RESOLUTION_PRECISION_X / 100) * containerWidth;
  const COLLISION_RESOLUTION_PRECISION_Y_PIXELS = (COLLISION_RESOLUTION_PRECISION_Y / 100) * containerHeight;
  const DIRECTION_RESOLUTION_X_PIXELS = (DIRECTION_RESOLUTION_X / 100) * containerWidth;
  const DIRECTION_RESOLUTION_Y_PIXELS = (DIRECTION_RESOLUTION_Y / 100) * containerHeight;

  // we want the max table height to lower when there are many players in the team
  const totalNumberOfPlayersInTeam = updatedNodes.reduce((acc, node) => acc + node.playerList.length, 0);
  const maxTableHeightPercentage = totalNumberOfPlayersInTeam > 120
    ? 0.4
    : totalNumberOfPlayersInTeam > 110
      ? 0.5
      : totalNumberOfPlayersInTeam > 100
        ? 0.6
        : 0.7;
  const maxTableHeight = (containerHeight - (SOFT_MARGIN_EDGES_Y_PIXELS * 2)) * maxTableHeightPercentage;

  // convert percentage positions to pixels and initialize table properties, considering the container bounds constraints
  const nodesInPixels: TableNode[] = updatedNodes.map(node => {
    const width = tableWidth;
    let height = tableRowHeight * Math.max(node.playerList.length, 1); // if table is empty, we set minimum height to 1 row (drop zone)
    if (isRoleConfigTable && node.playerList.length > 1) {
      height += roleConfigAggregationRuleHeight;
    }

    // if height is greater than 75% of the screen height, we immediately cap it
    const desiredHeight = height;
    while (height > maxTableHeight && height > tableRowHeight * 2) {
      height -= tableRowHeight;
    }

    // enforce container bounds in pixels
    const minXPx = width / 2 + SOFT_MARGIN_EDGES_X_PIXELS;
    const maxXPx = containerWidth - width / 2 - SOFT_MARGIN_EDGES_X_PIXELS;
    const minYPx = height / 2 + SOFT_MARGIN_EDGES_Y_PIXELS;
    const maxYPx = containerHeight - height / 2 - SOFT_MARGIN_EDGES_Y_PIXELS;

    const isCentered = Math.abs(node.left - 50) < 1;
    const originalLeftPx = (node.left / 100) * containerWidth;
    const leftPx = isCentered
      ? originalLeftPx
      : Math.max(minXPx, Math.min(maxXPx, originalLeftPx));

    const originalTopPx = (node.top / 100) * containerHeight;
    const topPx = Math.max(minYPx, Math.min(maxYPx, originalTopPx));

    return {
      ...node,
      leftPx,
      topPx,
      originalLeftPx,
      originalTopPx,
      isCentered,
      width,
      height,
      desiredHeight,
      movementResistance: {
        horizontal: 1,
        vertical: 1
      },
      safeMarginLeft: 0,
      safeMarginRight: 0,
      safeMarginTop: 0,
      safeMarginBottom: 0,
      numberOfXRelaxations: 0,
      numberOfYRelaxations: 0,
      numberOfHeightRelaxations: 0,

      lowestSafeMarginXToNodes: 0,
      safeMarginLeftToNodes: Infinity,
      safeMarginRightToNodes: Infinity,
    };
  });


  const stableIterationThresholdToMoveInEitherDirection = 1;
  const stableIterationThresholdToExitWithoutCollisions = 2;
  const stableIterationThresholdToIncreaseMaxPositionDeviation = 2;
  const stableIterationThresholdToStartHeightCapping = 3;
  const stableIterationThresholdToExit = 4; // must be greater than all above

  while (iterations < MAX_ITERATIONS) {

    // we exit after 2 stable iterations in a row without collisions, or after 5 stable iterations in a row after starting height capping
    if (stableIterationCount >= stableIterationThresholdToExitWithoutCollisions && !hasCollisions) {
      break;
    }
    if (stableIterationCount >= stableIterationThresholdToExit) {
      break;
    }

    // if we have been stable for 2 iterations after starting height capping, we increase the max position deviation
    if (stableIterationCount >= stableIterationThresholdToIncreaseMaxPositionDeviation && heightCappingHasStarted) {
      if (maxPositionDeviationXPixels < MAX_POSITION_DEVIATION_X_PIXELS * 2) {
        maxPositionDeviationXPixels += containerWidth * 0.01;
      }
      if (maxPositionDeviationYPixels < MAX_POSITION_DEVIATION_Y_PIXELS * 2) {
        maxPositionDeviationYPixels += containerHeight * 0.01;
      }
    }

    if (
      !heightCappingHasStarted &&
      (stableIterationCount >= stableIterationThresholdToStartHeightCapping || iterations >= HEIGHT_CAPPING_STARTING_ITERATION)
    ) {
      heightCappingHasStarted = true;
    }


    // momentum factor will decrease every iteration
    momentumFactorToUse = Math.max(MOMENTUM_FACTOR * 0.5, MOMENTUM_FACTOR - (iterations * 0.01));


    iterations++;
    hasCollisions = false;


    // initialize movement tracking for all nodes
    const nodeMovements: NodeMovement[] = nodesInPixels.map(() => ({
      moveLeft: 0,
      moveRight: 0,
      moveUp: 0,
      moveDown: 0,
      heightDecrease: false
    }));


    // update movement resistance based on deviation
    for (let i = 0; i < nodesInPixels.length; i++) {
      const node = nodesInPixels[i];
      const horizontalDeviation = Math.abs(node.leftPx - node.originalLeftPx);
      const verticalDeviation = Math.abs(node.topPx - node.originalTopPx);

      node.movementResistance = {
        horizontal: maxPositionDeviationXPixels === 0 || horizontalDeviation >= maxPositionDeviationXPixels
          ? 0
          : 1 - (horizontalDeviation / maxPositionDeviationXPixels),
        vertical: maxPositionDeviationYPixels === 0 || verticalDeviation >= maxPositionDeviationYPixels
          ? 0
          : 1 - (verticalDeviation / maxPositionDeviationYPixels)
      };

      // set initial safe margins to edges
      node.safeMarginLeft = node.leftPx - (node.width / 2 + SOFT_MARGIN_EDGES_X_PIXELS);
      node.safeMarginRight = containerWidth - node.leftPx - (node.width / 2 + SOFT_MARGIN_EDGES_X_PIXELS);
      node.safeMarginTop = node.topPx - (node.height / 2 + SOFT_MARGIN_EDGES_Y_PIXELS);
      node.safeMarginBottom = containerHeight - node.topPx - (node.height / 2 + SOFT_MARGIN_EDGES_Y_PIXELS);

      node.safeMarginLeftToNodes = Infinity;
      node.safeMarginRightToNodes = Infinity;
    }


    // check for collisions with other nodes
    for (let i = 0; i < nodesInPixels.length; i++) {
      const nodeA = nodesInPixels[i];

      for (let j = i + 1; j < nodesInPixels.length; j++) {
        const nodeB = nodesInPixels[j];

        // calculate the different overlaps in X and Y direction
        const actualOverlapX = (nodeA.width + nodeB.width) / 2 - Math.abs(nodeA.leftPx - nodeB.leftPx);
        const actualOverlapY = (nodeA.height + nodeB.height) / 2 - Math.abs(nodeA.topPx - nodeB.topPx);

        const collisionOverlapX = actualOverlapX + SOFT_MARGIN_X_PIXELS;
        const collisionOverlapY = actualOverlapY + SOFT_MARGIN_Y_PIXELS;

        const orthogonalOverlapX = actualOverlapX + (SOFT_MARGIN_X_PIXELS / 2);
        const orthogonalOverlapY = actualOverlapY + (SOFT_MARGIN_Y_PIXELS / 2);

        if (collisionOverlapX > COLLISION_RESOLUTION_PRECISION_X_PIXELS && collisionOverlapY > COLLISION_RESOLUTION_PRECISION_Y_PIXELS) {
          hasCollisions = true;

          if (!isRoleConfigTable && heightCappingHasStarted) {

            // only consider height reduction if table has more than 2 rows and the node has not already been capped
            const nodeAHeightCanBeCapped = nodeA.height > tableRowHeight * 2;
            const nodeBHeightCanBeCapped = nodeB.height > tableRowHeight * 2;

            // we only consider height reduction if the nodes have not already been capped
            if (nodeAHeightCanBeCapped || nodeBHeightCanBeCapped) {

              // we assume height capping to help as long as the collision is not between two nodes with the same y position
              const nodesHaveSameYPosition = nodeA.topPx === nodeB.topPx;

              // we also assume height capping to help only if the nodes do not collide on the sides
              // we want to be more strict (higher factor) when iterations is low
              const iterationsFactor = Math.max(1, 5 - ((iterations - HEIGHT_CAPPING_STARTING_ITERATION) * 0.1));

              // we want to be more strict (higher factor) when the number of extra players is low
              const maxNumberOfExtraPlayers = Math.max(0, Math.max(nodeA.playerList.length, nodeB.playerList.length) - 2);
              const heightFactor = maxNumberOfExtraPlayers <= 1 ? 8 : maxNumberOfExtraPlayers <= 2 ? 4 : maxNumberOfExtraPlayers <= 3 ? 2 : 1;

              const sideCollisionThreshold = 0.01 * iterationsFactor * heightFactor;
              const collisionIsSideCollision = (collisionOverlapX / collisionOverlapY) < sideCollisionThreshold;
              const heightCappingCouldHelp = !nodesHaveSameYPosition && !collisionIsSideCollision;

              if (heightCappingCouldHelp) {

                // we prefer to cap the taller node, but if they have the same height, we cap the node with the most players
                const shouldCapNodeA = nodeAHeightCanBeCapped && (
                  nodeA.height > nodeB.height || (nodeA.height === nodeB.height && nodeA.playerList.length >= nodeB.playerList.length)
                );

                if (shouldCapNodeA) {
                  nodeMovements[i].heightDecrease = true;
                }
                else {
                  nodeMovements[j].heightDecrease = true;
                }
              }
            }
          }

          // on collision, we dont allow horizontal movement if original xCoordinates are the same, and vertical movement if original yCoordinates are the same
          const nodesCanMoveHorizontally = nodeA.originalLeftPx !== nodeB.originalLeftPx;
          const nodesCanMoveVertically = nodeA.originalTopPx !== nodeB.originalTopPx;

          // calculate movements - we only move in one direction, based on the relative size of the overlap, unless the last X iterations was stable
          if (nodesCanMoveHorizontally && (
            collisionOverlapX <= collisionOverlapY || !nodesCanMoveVertically || stableIterationCount >= stableIterationThresholdToMoveInEitherDirection)
          ) {

            // if we are very close to resolving the collision, we don't apply resistance or momentum
            const weAreCloseToResolve = collisionOverlapX < CLOSE_TO_RESOLVE_OVERLAP_THRESHOLD;

            // direction is determined by a combination of the original x positions of the nodes and the current x positions
            // if the original are close enough, we use the current x positions to determine the direction, otherwise we use the original positions
            // this is to avoid tables moving past other tables and staying stuck on the wrong side of other tables
            // direction is 1 if nodeA is to the left of nodeB, that is nodeA should move to the left
            let direction = 0;
            if (Math.abs(nodeB.originalLeftPx - nodeA.originalLeftPx) > DIRECTION_RESOLUTION_X_PIXELS) {
              direction = Math.sign(nodeB.originalLeftPx - nodeA.originalLeftPx);
            }
            else {
              direction = Math.sign(nodeB.leftPx - nodeA.leftPx);
            }
            if (direction === 0) direction = Math.sign(nodeB.originalLeftPx - nodeA.originalLeftPx);
            if (direction === 0) direction = Math.sign(nodeB.topPx - nodeA.topPx);


            // if we are moving towards the original position, we don't apply resistance
            const nodeAIsMovingTowardsOriginalPosition = direction > 0
              ? nodeA.originalLeftPx < nodeA.leftPx // nodeA is moving left
              : nodeA.originalLeftPx > nodeA.leftPx; // nodeA is moving right

            const nodeBIsMovingTowardsOriginalPosition = direction < 0
              ? nodeB.originalLeftPx < nodeB.leftPx // nodeB is moving left
              : nodeB.originalLeftPx > nodeB.leftPx; // nodeB is moving right

            const resistanceA = weAreCloseToResolve || nodeAIsMovingTowardsOriginalPosition ? 1 : nodeA.movementResistance.horizontal;
            const resistanceB = weAreCloseToResolve || nodeBIsMovingTowardsOriginalPosition ? 1 : nodeB.movementResistance.horizontal;

            const distanceA = collisionOverlapX * resistanceA;
            const distanceB = collisionOverlapX * resistanceB;

            // both nodes can move, split the movement
            if (!nodeA.isCentered && !nodeB.isCentered) {
              if (direction > 0) { // nodeA is to the left of nodeB
                nodeMovements[i].moveLeft = Math.max(nodeMovements[i].moveLeft, distanceA * 0.5);
                nodeMovements[j].moveRight = Math.max(nodeMovements[j].moveRight, distanceB * 0.5);
              }
              else {
                nodeMovements[i].moveRight = Math.max(nodeMovements[i].moveRight, distanceA * 0.5);
                nodeMovements[j].moveLeft = Math.max(nodeMovements[j].moveLeft, distanceB * 0.5);
              }

            }

            // only B can move, move full distance, to avoid asymptotical behavior
            else if (nodeA.isCentered) {
              if (direction > 0) { // nodeA is to the left of nodeB
                nodeMovements[j].moveRight = Math.max(nodeMovements[j].moveRight, distanceB);
              }
              else {
                nodeMovements[j].moveLeft = Math.max(nodeMovements[j].moveLeft, distanceB);
              }
            }

            // only A can move, move full distance, to avoid asymptotical behavior
            else {
              if (direction > 0) { // nodeA is to the left of nodeB
                nodeMovements[i].moveLeft = Math.max(nodeMovements[i].moveLeft, distanceA);
              }
              else {
                nodeMovements[i].moveRight = Math.max(nodeMovements[i].moveRight, distanceA);
              }
            }
          }

          if (nodesCanMoveVertically && (
            collisionOverlapX > collisionOverlapY || !nodesCanMoveHorizontally || stableIterationCount >= stableIterationThresholdToMoveInEitherDirection)
          ) {

            // if we are very close to resolving the collision, we don't apply resistance, to avoid asymptotical behavior
            const weAreCloseToResolve = collisionOverlapY < CLOSE_TO_RESOLVE_OVERLAP_THRESHOLD;

            // direction is determined by a combination of the original y positions of the nodes and the current y positions
            // if the original are close enough, we use the current y positions to determine the direction, otherwise we use the original positions
            // this is to avoid tables moving past other tables and staying stuck on the wrong side of other tables
            // direction is 1 if nodeA is above nodeB, that is nodeA should move down
            let direction = 0;
            if (Math.abs(nodeB.originalTopPx - nodeA.originalTopPx) > DIRECTION_RESOLUTION_Y_PIXELS) {
              direction = Math.sign(nodeB.originalTopPx - nodeA.originalTopPx);
            }
            else {
              direction = Math.sign(nodeB.topPx - nodeA.topPx);
            }
            if (direction === 0) direction = Math.sign(nodeB.originalTopPx - nodeA.originalTopPx);
            if (direction === 0) direction = Math.sign(nodeB.leftPx - nodeA.leftPx);


            // if we are moving towards the original position, we don't apply resistance
            const nodeAIsMovingTowardsOriginalPosition = direction > 0
              ? nodeA.originalTopPx < nodeA.topPx // nodeA is moving down
              : nodeA.originalTopPx > nodeA.topPx; // nodeA is moving up
            const nodeBIsMovingTowardsOriginalPosition = direction < 0
              ? nodeB.originalTopPx < nodeB.topPx // nodeB is moving down
              : nodeB.originalTopPx > nodeB.topPx; // nodeB is moving up

            const resistanceA = weAreCloseToResolve || nodeAIsMovingTowardsOriginalPosition ? 1 : nodeA.movementResistance.vertical;
            const resistanceB = weAreCloseToResolve || nodeBIsMovingTowardsOriginalPosition ? 1 : nodeB.movementResistance.vertical;

            const distanceA = collisionOverlapY * resistanceA;
            const distanceB = collisionOverlapY * resistanceB;

            // nodeB is above nodeA
            if (direction > 0) { // nodeA is above nodeB
              nodeMovements[i].moveUp = Math.max(nodeMovements[i].moveUp, distanceA * 0.5);
              nodeMovements[j].moveDown = Math.max(nodeMovements[j].moveDown, distanceB * 0.5);
            }

            // nodeA is above nodeB
            else { // nodeA is above nodeB
              nodeMovements[i].moveDown = Math.max(nodeMovements[i].moveDown, distanceA * 0.5);
              nodeMovements[j].moveUp = Math.max(nodeMovements[j].moveUp, distanceB * 0.5);
            }
          }
        }

        // update safe margins for both nodes
        if (orthogonalOverlapY > COLLISION_RESOLUTION_PRECISION_Y_PIXELS) {
          // there are close enough in y direction to affect each other's safe margin in x direction
          if (nodeA.leftPx <= nodeB.leftPx) {
            nodeA.safeMarginRight = Math.min(nodeA.safeMarginRight, -collisionOverlapX);
            nodeB.safeMarginLeft = Math.min(nodeB.safeMarginLeft, -collisionOverlapX);

            nodeA.safeMarginRightToNodes = Math.min(nodeA.safeMarginRightToNodes, -collisionOverlapX);
            nodeB.safeMarginLeftToNodes = Math.min(nodeB.safeMarginLeftToNodes, -collisionOverlapX);
          }
          if (nodeA.leftPx >= nodeB.leftPx) {
            nodeA.safeMarginLeft = Math.min(nodeA.safeMarginLeft, -collisionOverlapX);
            nodeB.safeMarginRight = Math.min(nodeB.safeMarginRight, -collisionOverlapX);

            nodeA.safeMarginLeftToNodes = Math.min(nodeA.safeMarginLeftToNodes, -collisionOverlapX);
            nodeB.safeMarginRightToNodes = Math.min(nodeB.safeMarginRightToNodes, -collisionOverlapX);
          }
        }

        if (orthogonalOverlapX > COLLISION_RESOLUTION_PRECISION_X_PIXELS) {
          // there are close enough in x direction to affect each other's safe margin in y direction
          if (nodeA.topPx <= nodeB.topPx) {
            nodeA.safeMarginBottom = Math.min(nodeA.safeMarginBottom, -collisionOverlapY);
            nodeB.safeMarginTop = Math.min(nodeB.safeMarginTop, -collisionOverlapY);
          }
          if (nodeA.topPx >= nodeB.topPx) {
            nodeA.safeMarginTop = Math.min(nodeA.safeMarginTop, -collisionOverlapY);
            nodeB.safeMarginBottom = Math.min(nodeB.safeMarginBottom, -collisionOverlapY);
          }
        }
      }
    }

    stableIterationCount++;

    // apply all movements
    for (let i = 0; i < nodesInPixels.length; i++) {
      const node = nodesInPixels[i];
      const movement = nodeMovements[i];

      const desiredMovementX = movement.moveRight - movement.moveLeft;
      const desiredMovementY = movement.moveDown - movement.moveUp;

      let actualMovementX = 0;
      let actualMovementY = 0;
      let actualHeightDecrease = false;
      let actualHeightIncrease = false;

      // apply horizontal movement
      if (!node.isCentered) {
        const minXPx = tableWidth / 2 + SOFT_MARGIN_EDGES_X_PIXELS;
        const maxXPx = containerWidth - tableWidth / 2 - SOFT_MARGIN_EDGES_X_PIXELS;

        // if movement is low, or we have enough safe margin in the movement direction, we don't apply momentum
        let movementX = desiredMovementX;
        const isCloseToResolve = Math.abs(movementX) < CLOSE_TO_RESOLVE_OVERLAP_THRESHOLD;
        const hasEnoughSafeMargin = movementX < 0 ? Math.abs(movementX) < node.safeMarginLeft : Math.abs(movementX) < node.safeMarginRight;
        movementX = movementX * (isCloseToResolve || hasEnoughSafeMargin ? 1 : momentumFactorToUse);

        const newLeftPx = Math.max(minXPx, Math.min(maxXPx, node.leftPx + movementX));
        if (newLeftPx !== node.leftPx) {
          const cappedMovementX = newLeftPx - node.leftPx;
          if (Math.abs(cappedMovementX) > MOVEMENT_RESOLUTION_PRECISION) {
            actualMovementX = cappedMovementX;
            node.leftPx = newLeftPx;
          }
        }
      }


      // apply vertical movement
      const minYPx = node.height / 2 + SOFT_MARGIN_EDGES_Y_PIXELS;
      const maxYPx = containerHeight - node.height / 2 - SOFT_MARGIN_EDGES_Y_PIXELS;

      // if movement is low, or we have enough safe margin in the movement direction, we don't apply momentum
      let movementY = desiredMovementY;
      const isCloseToResolve = Math.abs(movementY) < CLOSE_TO_RESOLVE_OVERLAP_THRESHOLD;
      const hasEnoughSafeMargin = movementY < 0 ? Math.abs(movementY) < node.safeMarginTop : Math.abs(movementY) < node.safeMarginBottom;
      movementY = movementY * (isCloseToResolve || hasEnoughSafeMargin ? 1 : momentumFactorToUse);

      const newTopPx = Math.max(minYPx, Math.min(maxYPx, node.topPx + movementY));
      if (newTopPx !== node.topPx) {
        const cappedMovementY = newTopPx - node.topPx;
        if (Math.abs(cappedMovementY) > MOVEMENT_RESOLUTION_PRECISION) {
          actualMovementY = cappedMovementY;
          node.topPx = newTopPx;
        }
      }


      // apply height decrease if needed, but never below 2 rows
      if (movement.heightDecrease && node.height > tableRowHeight * 2) {
        node.height -= tableRowHeight;
        actualHeightDecrease = true;

        // try to move back towards original position if we've moved vertically
        const verticalDeviation = node.topPx - node.originalTopPx;
        if (Math.abs(verticalDeviation) > 0) {
          // move back to original position, but not more than one table row height
          const moveBackAmount = Math.min(verticalDeviation, tableRowHeight);
          const minYPx = node.height / 2 + SOFT_MARGIN_EDGES_Y_PIXELS;
          const maxYPx = containerHeight - node.height / 2 - SOFT_MARGIN_EDGES_Y_PIXELS;

          const newTopPx = Math.max(minYPx, Math.min(maxYPx, node.topPx - moveBackAmount));
          const movementY = newTopPx - node.topPx;
          if (Math.abs(movementY) > MOVEMENT_RESOLUTION_PRECISION) {
            actualMovementY += movementY;
            node.topPx = newTopPx;
          }
        }
      }

      // we only want to relax when we have not moved
      if (actualMovementX === 0 && actualMovementY === 0 && !actualHeightDecrease) {
        // we only want to relax in one direction each iteration, and we want to relax in the most significant direction relative to the original position
        const horizontalDeviation = node.leftPx - node.originalLeftPx;
        const verticalDeviation = node.topPx - node.originalTopPx;

        let desiredRelaxationX = 0;
        let desiredRelaxationY = 0;

        const canRelaxInX = horizontalDeviation !== 0 && node.numberOfXRelaxations <= 3; // we only allow 3 relaxations in each direction
        const canRelaxInY = verticalDeviation !== 0 && node.numberOfYRelaxations <= 3; // we only allow 3 relaxations in each direction

        // calculate desired relaxation in X direction
        if (canRelaxInX) {

          // if negative, node wants to relax to the left, and vice versa
          const directionToOriginal = Math.sign(node.originalLeftPx - node.leftPx);
          const safeMarginInDirection = directionToOriginal === -1 ? node.safeMarginLeft : node.safeMarginRight;

          if (safeMarginInDirection > SAFE_MARGIN) {
            const preferedDistance = Math.min(safeMarginInDirection, Math.abs(horizontalDeviation));
            const moveX = Math.min(Math.abs(horizontalDeviation), preferedDistance) * directionToOriginal;

            if (Math.abs(moveX) > MOVEMENT_RESOLUTION_PRECISION) {
              desiredRelaxationX = moveX;
            }
          }
        }

        // apply safe margin relaxation in Y direction if possible
        if (canRelaxInY) {

          // if negative, node wants to relax to the top, and vice versa
          const directionToOriginal = Math.sign(node.originalTopPx - node.topPx);
          const safeMarginInDirection = directionToOriginal === -1 ? node.safeMarginTop : node.safeMarginBottom;

          if (safeMarginInDirection > SAFE_MARGIN) {
            const preferedDistance = Math.min(safeMarginInDirection, Math.abs(verticalDeviation));
            const moveY = Math.min(Math.abs(verticalDeviation), preferedDistance) * directionToOriginal;

            if (Math.abs(moveY) > MOVEMENT_RESOLUTION_PRECISION) {
              desiredRelaxationY = moveY;
            }
          }
        }

        const desiredRelaxationDirection = Math.abs(horizontalDeviation) > Math.abs(verticalDeviation) && desiredRelaxationX !== 0
          ? 'horizontal'
          : desiredRelaxationY !== 0
            ? 'vertical'
            : undefined;

        if (desiredRelaxationDirection === 'horizontal') {
          node.leftPx += desiredRelaxationX;
          node.numberOfXRelaxations++;
          actualMovementX = desiredRelaxationX;
        }
        if (desiredRelaxationDirection === 'vertical') {
          node.topPx += desiredRelaxationY;
          node.numberOfYRelaxations++;
          actualMovementY = desiredRelaxationY;
        }
      }

      // relax in height
      if (
        actualMovementX === 0 &&
        actualMovementY === 0 &&
        !actualHeightDecrease &&
        node.height < node.desiredHeight &&
        node.height < (maxTableHeight - tableRowHeight) &&
        node.numberOfHeightRelaxations <= 3
      ) {

        // if we have margin in both y directions, we increase the height, and we do not need to adjust position
        if (node.safeMarginTop > (tableRowHeight / 2) && node.safeMarginBottom > (tableRowHeight / 2)) {
          node.height += tableRowHeight;
          actualHeightDecrease = true;
        }

        // if we have margin in one direction, we increase the height if we can move accordingly without violating the deviation
        else {
          if (node.safeMarginTop > tableRowHeight) {
            const deviationAfterHeightIncrease = Math.abs((node.topPx + tableRowHeight / 2) - node.originalTopPx);
            if (deviationAfterHeightIncrease < MAX_POSITION_DEVIATION_Y_PIXELS) {
              node.height += tableRowHeight;
              node.topPx -= tableRowHeight / 2;
              actualMovementY = tableRowHeight / 2;
              actualHeightIncrease = true;
              node.numberOfHeightRelaxations++;
            }
          }

          else if (node.safeMarginBottom > tableRowHeight) {
            const deviationAfterHeightIncrease = Math.abs((node.topPx + tableRowHeight / 2) - node.originalTopPx);
            if (deviationAfterHeightIncrease < MAX_POSITION_DEVIATION_Y_PIXELS) {
              node.height += tableRowHeight;
              node.topPx += tableRowHeight / 2;
              actualMovementY = tableRowHeight / 2;
              actualHeightIncrease = true;
              node.numberOfHeightRelaxations++;
            }
          }
        }
      }


      if (actualMovementX !== 0 || actualMovementY !== 0 || actualHeightDecrease || actualHeightIncrease) {
        stableIterationCount = 0;
      }
    }
  }

  // convert back to percentages for rendering
  return nodesInPixels.map(node => {
    return {
      ...node,
      left: (node.leftPx / containerWidth) * 100,
      top: (node.topPx / containerHeight) * 100,
      lowestSafeMarginXToNodes: Math.min(node.safeMarginLeftToNodes, node.safeMarginRightToNodes),
    };
  });
};


// get team that is aligned with the formation config
// normal teams should always be aligned, but archived teams may not be
// if team needs to be aligned, we return the aligned teams and update store
export const getAlignedTeam = (
  teamId: string | 'ownTeamPlanner' | 'archivedTeam' | 'roleConfigs',
  team: Team | ArchivedTeam | RoleConfigTeam,
  formationConfig: FormationConfig,
  currentUser: FirebaseUser,
): Team | ArchivedTeam | RoleConfigTeam => {

  // team is aligned if all players in the team have a position in the formation config
  const teamIsAligned = Object.entries(team.players).every(
    ([positionKey, playerList]) => (!playerList || playerList.length === 0 || formationConfig[positionKey as PlatformPosition] !== undefined)
  );

  if (teamIsAligned) return team;

  trackEvent(
    'UnexpectedBehavior',
    { file: 'teamViewUtils.ts', behavior: 'Team with teamId ' + teamId + ' is not aligned with formation config' },
    currentUser,
    'user'
  );

  // 1. role configs
  if (teamId === 'roleConfigs') {
    team = team as RoleConfigTeam;
    const newRoleConfigTeamPlayers: RoleConfigTeamPlayers = {};

    Object.entries(team.players).forEach(([positionKey, playerList]) => {
      if (formationConfig[positionKey as PlatformPosition] !== undefined) {
        newRoleConfigTeamPlayers[positionKey] = playerList;
      }
    });

    return { ...team, players: newRoleConfigTeamPlayers };
  }

  // 2. teams and archived teams
  team = team as Team | ArchivedTeam;
  const newPlayers: TeamPlayers = {};

  Object.entries(team.players).forEach(([positionKey, playerList]) => {
    if (playerList && playerList.length > 0) {
      if (formationConfig[positionKey as PlatformPosition] !== undefined) {
        newPlayers[positionKey] = playerList;
      }

      else {
        // find the closest position in the formation config
        let closestPosition = positionToClosestPositions[positionKey as PlatformPosition].find(
          position => formationConfig[position as PlatformPosition] !== undefined
        );

        // default to the first position in the list
        if (!closestPosition) {
          closestPosition = positionToClosestPositions[positionKey as PlatformPosition][0];
        }

        newPlayers[closestPosition] = [...(newPlayers[closestPosition] ?? []), ...playerList];
      }
    }
  });

  return { ...team, players: newPlayers };
};
